import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircleFill, GeoAltFill, Globe, PeopleFill, PersonCheck, PersonPlus, ShieldCheck, XLg } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { createPrizeWheel, updatePrizeWheel } from '../../api/prizeWheels';
import { fetchBrandProducts } from '../../api/brands';
import {
  DEFAULT_ELIGIBILITY,
  ELIGIBILITY,
  PRIZE_MAX,
  TITLE_MAX,
  defaultDrawAt,
  errorText,
  fromDateTimeInputs,
  invalidFieldOf,
  quickDrawTimes,
  toDateInput,
  toTimeInput,
  validateWheelForm,
} from '../prizeWheelShape';
import { fmtDateTime, fmtNum, useEscape } from './wheelUi';
import RoundsBuilder, { newRound } from './RoundsBuilder';
import FreeNotice from './FreeNotice';
import { prizeCatalogue, roundSummary } from './wheelProducts';

// The schedule / edit dialog of the prize wheel, with the live 3D preview
// (WheelPreview — three.js in a lazy chunk) next to the form.

const WheelPreview = React.lazy(() => import(/* webpackChunkName: "wheel-preview" */ './WheelPreview'));

/** How many demo participants the preview shows. */
const PREVIEW_POINTS = 480;

function FieldError({ id, msg }) {
  if (!msg) return null;
  return <small id={id} className="wheel-form__err" role="alert">{msg}</small>;
}

/** Schedule (create) or edit a wheel. The live 3D preview sits next to the form. */
export default function WheelFormModal({ brand, wheel, serverNow, onClose, onDone, onConflict }) {
  const { t, lang } = useLocale();
  const edit = !!wheel;
  const now0 = useMemo(() => serverNow(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const initialDraw = edit ? wheel.drawAt : defaultDrawAt(now0);
  const [title, setTitle] = useState(edit ? wheel.title : '');
  const [prize, setPrize] = useState(edit ? wheel.prizeDescription : '');
  const [rounds, setRounds] = useState(() =>
    edit && wheel.rounds.length ? wheel.rounds.map((r) => newRound(r.productIds)) : [newRound()]
  );
  const [exclude, setExclude] = useState(edit ? wheel.excludePreviousWinners !== false : true);
  const [eligibility, setEligibility] = useState(edit ? wheel.eligibility || DEFAULT_ELIGIBILITY : DEFAULT_ELIGIBILITY);
  const [catalogue, setCatalogue] = useState(null); // null loading | product[] | 'error'
  const [opensNow, setOpensNow] = useState(true);
  const [opensDate, setOpensDate] = useState(toDateInput(now0 + 60 * 60 * 1000));
  const [opensTime, setOpensTime] = useState(toTimeInput(Math.ceil((now0 + 60 * 60 * 1000) / 3600000) * 3600000));
  const [drawDate, setDrawDate] = useState(toDateInput(initialDraw));
  const [drawTime, setDrawTime] = useState(toTimeInput(initialDraw));
  const [rules, setRules] = useState(false);
  const [freeAccepted, setFreeAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [serverField, setServerField] = useState(null);
  useEscape(onClose, busy);

  // The brand's own products (the Products tab's endpoint).
  useEffect(() => {
    let alive = true;
    fetchBrandProducts(brand.id)
      .then((list) => alive && setCatalogue(prizeCatalogue(list)))
      .catch(() => alive && setCatalogue('error'));
    return () => {
      alive = false;
    };
  }, [brand.id]);

  // id → product: the catalogue, plus the wheel's own products (edit) in case one left the catalogue.
  const products = useMemo(() => {
    const m = new Map();
    if (edit) for (const r of wheel.rounds) for (const p of r.products) m.set(p.id, p);
    if (Array.isArray(catalogue)) for (const p of catalogue) m.set(p.id, p);
    return m;
  }, [catalogue, edit, wheel]);
  const summary = rounds
    .map((r, i) => {
      const names = roundSummary(r.productIds.map((id) => products.get(id)).filter(Boolean));
      return names ? t('wheel.form.autoSummaryRound', { n: i + 1, names }) : null;
    })
    .filter(Boolean)
    .join(' · ');

  const opensAt = opensNow ? null : fromDateTimeInputs(opensDate, opensTime);
  // Edit: an untouched date + time keeps the stored draw time (the inputs only hold minutes).
  const drawUntouched = edit && drawDate === toDateInput(wheel.drawAt) && drawTime === toTimeInput(wheel.drawAt);
  const drawAt = drawUntouched ? wheel.drawAt : fromDateTimeInputs(drawDate, drawTime);
  const values = { title, prizeDescription: prize, rounds, excludePreviousWinners: exclude, eligibility, opensNow, opensAt, drawAt, rulesAccepted: rules, freeShippingAccepted: freeAccepted };
  const { errors, body } = validateWheelForm(values, serverNow(), { mode: edit ? 'edit' : 'create', original: wheel });
  const show = (field) => (submitted || (field === 'drawAt' && drawAt !== null) ? errors[field] && t(errors[field]) : null)
    || (serverField === field ? t('wheel.form.err.server') : null);
  const quick = quickDrawTimes(edit ? Math.max(wheel.opensAt ?? now0, now0) : opensAt ?? now0);

  function setDraw(ms) {
    setDrawDate(toDateInput(ms));
    setDrawTime(toTimeInput(ms));
  }

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setSubmitted(true);
    setServerField(null);
    if (Object.keys(errors).length) {
      setError(t('wheel.form.fix'));
      return;
    }
    if (edit && Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = edit ? await updatePrizeWheel(brand.id, wheel.id, body) : await createPrizeWheel(brand.id, body);
      onDone(result, edit);
    } catch (err) {
      setError(errorText(err, t));
      setServerField(invalidFieldOf(err));
      if (err?.status === 409) onConflict?.();
      setBusy(false);
    }
  }

  const drawLabel = drawAt !== null ? fmtDateTime(drawAt, lang, { weekday: true }) : null;

  return createPortal(
    <div className="gift-modal" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form className="gift-modal__dialog wheel-modal" role="dialog" aria-modal="true" aria-labelledby="wheel-form-title" onSubmit={submit} noValidate>
        <div className="gift-modal__head">
          <h3 id="wheel-form-title" className="gift-modal__title">{t(edit ? 'wheel.form.editTitle' : 'wheel.form.createTitle')}</h3>
          <button type="button" className="gift-modal__close" onClick={onClose} disabled={busy} aria-label={t('wheel.form.back')}>
            <XLg size={14} aria-hidden="true" />
          </button>
        </div>

        <div className="wheel-modal__body">
          <aside className="wheel-modal__side">
            <Suspense fallback={<div className="wheel-viewer wheel-viewer--fallback" />}>
              <WheelPreview
                logoUrl={brand.logo || null}
                brandColor={brand.brandColor ?? brand.brand_color ?? null}
                brandName={brand.name}
                points={PREVIEW_POINTS}
                count={PREVIEW_POINTS}
              >
                <span className={`wheel-chip${title.trim() ? '' : ' wheel-chip--muted'}`}>{title.trim() || t('wheel.form.titlePreview')}</span>
                <span className="wheel-chip wheel-chip--muted">{t('wheel.preview.roundsChip', { n: rounds.length })}</span>
                {drawLabel && <span className="wheel-chip wheel-chip--muted">{t('wheel.preview.drawChip', { at: drawLabel })}</span>}
              </WheelPreview>
            </Suspense>
            <p className="wheel-modal__caption">{t('wheel.preview.caption', { n: fmtNum(PREVIEW_POINTS, lang) })}</p>
            <ol className="wheel-steps">
              <li>
                <span className="wheel-steps__icon"><GeoAltFill size={14} /></span>
                <span><strong>{t('wheel.how.mapTitle')}</strong> {t('wheel.how.map')}</span>
              </li>
              <li>
                <span className="wheel-steps__icon"><PeopleFill size={14} /></span>
                <span><strong>{t('wheel.how.joinTitle')}</strong> {t('wheel.how.join')}</span>
              </li>
              <li>
                <span className="wheel-steps__icon"><ShieldCheck size={14} /></span>
                <span><strong>{t('wheel.how.drawTitle')}</strong> {t('wheel.how.draw')}</span>
              </li>
            </ol>
          </aside>

          <div className="wheel-modal__form">
            <label className="gift-modal__field wheel-form__field">
              <span>{t('wheel.form.title')}</span>
              <input
                type="text"
                maxLength={TITLE_MAX}
                value={title}
                placeholder={t('wheel.form.titlePlaceholder')}
                onChange={(e) => setTitle(e.target.value)}
                disabled={busy}
                aria-invalid={!!show('title')}
                aria-describedby="wheel-title-err"
              />
              <small className="gift-modal__count">{title.length}/{TITLE_MAX}</small>
              <FieldError id="wheel-title-err" msg={show('title')} />
            </label>

            <div className="gift-modal__field wheel-form__field">
              <span>{t('wheel.form.rounds')}</span>
              <small className="gift-modal__hint wheel-form__lead">{t('wheel.form.roundsHint')}</small>
              <RoundsBuilder
                rounds={rounds}
                onChange={setRounds}
                products={products}
                catalogue={catalogue}
                disabled={busy}
                invalid={!!show('rounds')}
              />
              <FieldError id="wheel-rounds-err" msg={show('rounds')} />
            </div>

            <label className={`gift-modal__toggle${exclude ? ' is-on' : ''}`}>
              <input type="checkbox" role="switch" checked={exclude} onChange={(e) => setExclude(e.target.checked)} disabled={busy} />
              <span className="gift-modal__switch" aria-hidden="true" />
              <span className="gift-modal__toggle-text">
                <strong>{t('wheel.form.exclude')}</strong>
                <span>{t('wheel.form.excludeHint')}</span>
              </span>
            </label>

            <label className="gift-modal__field wheel-form__field">
              <span>{t('wheel.form.prizeOptional')}</span>
              <textarea
                rows={2}
                maxLength={PRIZE_MAX}
                value={prize}
                placeholder={summary || t('wheel.form.prizePlaceholder')}
                onChange={(e) => setPrize(e.target.value)}
                disabled={busy}
                aria-invalid={!!show('prizeDescription')}
                aria-describedby="wheel-prize-err"
              />
              <small className="gift-modal__hint">{t('wheel.form.prizeAutoHint')}</small>
              <FieldError id="wheel-prize-err" msg={show('prizeDescription')} />
            </label>

            <div className="gift-modal__field wheel-form__field">
              <span id="wheel-elig-label">{t('wheel.form.eligibility')}</span>
              <div className="wheel-elig" role="radiogroup" aria-labelledby="wheel-elig-label">
                {ELIGIBILITY.map((e) => {
                  const Icon = e === 'everyone' ? Globe : e === 'followers' ? PersonPlus : PersonCheck;
                  const on = eligibility === e;
                  return (
                    <button key={e} type="button" role="radio" aria-checked={on} className={`wheel-elig__opt${on ? ' is-on' : ''}`} onClick={() => setEligibility(e)} disabled={busy}>
                      <span className="wheel-elig__icon" aria-hidden="true"><Icon size={15} /></span>
                      <span className="wheel-elig__text">
                        <strong>{t(`wheel.eligibility.${e}`)}</strong>
                        <span>{t(`wheel.eligibility.${e}Hint`)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <FieldError id="wheel-elig-err" msg={show('eligibility')} />
            </div>

            <div className="gift-modal__field wheel-form__field">
              <span>{t('wheel.form.opens')}</span>
              {edit ? (
                <div className="wheel-form__readonly">{fmtDateTime(wheel.opensAt, lang, { weekday: true })}</div>
              ) : (
                <>
                  <div className="wheel-seg" role="radiogroup" aria-label={t('wheel.form.opens')}>
                    <button type="button" role="radio" aria-checked={opensNow} className={opensNow ? 'is-on' : ''} onClick={() => setOpensNow(true)} disabled={busy}>
                      {t('wheel.form.opensNow')}
                    </button>
                    <button type="button" role="radio" aria-checked={!opensNow} className={!opensNow ? 'is-on' : ''} onClick={() => setOpensNow(false)} disabled={busy}>
                      {t('wheel.form.opensLater')}
                    </button>
                  </div>
                  {!opensNow && (
                    <div className="wheel-form__dt">
                      <input type="date" value={opensDate} min={toDateInput(now0)} onChange={(e) => setOpensDate(e.target.value)} disabled={busy} aria-label={t('wheel.form.opensDate')} />
                      <input type="time" value={opensTime} onChange={(e) => setOpensTime(e.target.value)} disabled={busy} aria-label={t('wheel.form.opensTime')} />
                    </div>
                  )}
                </>
              )}
              <FieldError id="wheel-opens-err" msg={show('opensAt')} />
            </div>

            <div className="gift-modal__field wheel-form__field">
              <span>{t('wheel.form.draw')}</span>
              <div className="wheel-form__dt">
                <input
                  type="date"
                  value={drawDate}
                  min={toDateInput(now0)}
                  onChange={(e) => setDrawDate(e.target.value)}
                  disabled={busy}
                  aria-label={t('wheel.form.drawDate')}
                  aria-invalid={!!show('drawAt')}
                />
                <input
                  type="time"
                  value={drawTime}
                  onChange={(e) => setDrawTime(e.target.value)}
                  disabled={busy}
                  aria-label={t('wheel.form.drawTime')}
                  aria-invalid={!!show('drawAt')}
                />
              </div>
              <div className="wheel-form__quick">
                {quick.map((q) => (
                  <button key={q.key} type="button" className="wheel-quick" onClick={() => setDraw(q.ms)} disabled={busy}>
                    {t(`wheel.form.quick.${q.key}`)}
                  </button>
                ))}
              </div>
              <small className="gift-modal__hint">{t('wheel.form.drawHint')}</small>
              <FieldError id="wheel-draw-err" msg={show('drawAt')} />
            </div>

            <FreeNotice />
            {!edit && (
              <>
                <label className={`wheel-rules wheel-free-accept${freeAccepted ? ' is-on' : ''}${show('freeShippingAccepted') ? ' is-invalid' : ''}`}>
                  <input type="checkbox" checked={freeAccepted} onChange={(e) => setFreeAccepted(e.target.checked)} disabled={busy} />
                  <span className="wheel-rules__box" aria-hidden="true">{freeAccepted && <CheckCircleFill size={16} />}</span>
                  <span className="wheel-rules__text">
                    <strong>{t('wheel.free.accept')}</strong>
                  </span>
                </label>
                <FieldError id="wheel-free-err" msg={show('freeShippingAccepted')} />
              </>
            )}

            {!edit && (
              <label className={`wheel-rules${rules ? ' is-on' : ''}${show('rulesAccepted') ? ' is-invalid' : ''}`}>
                <input type="checkbox" checked={rules} onChange={(e) => setRules(e.target.checked)} disabled={busy} />
                <span className="wheel-rules__box" aria-hidden="true">{rules && <CheckCircleFill size={16} />}</span>
                <span className="wheel-rules__text">
                  <strong>{t('wheel.form.rules')}</strong>
                  <span>{t('wheel.form.rulesHint')}</span>
                </span>
              </label>
            )}
            <FieldError id="wheel-rules-err" msg={show('rulesAccepted')} />
          </div>
        </div>

        {error && <div className="gift-modal__error" role="alert">{error}</div>}

        <div className="gift-modal__actions">
          <button type="button" className="btn-o btn-o--ghost" onClick={onClose} disabled={busy}>
            {t('wheel.form.back')}
          </button>
          <button type="submit" className="btn-o btn-o--brand" disabled={busy}>
            {busy ? t(edit ? 'wheel.form.saving' : 'wheel.form.scheduling') : t(edit ? 'wheel.form.save' : 'wheel.form.schedule')}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

